import type { OrderDetailDTO } from "@/modules/orders/domain/types";
import { normalizeStockUnitLabel } from "@/modules/products/domain/unit-conversion";
import {
  formatDeliverySummary,
  paymentTypeLabel,
} from "@/modules/orders/presentation/ui/order-status";
import { displayCustomerAddress } from "@/modules/customers/domain/address";
import { formatLak } from "@/shared/lib/currency";
import type { ShopSettingsDTO } from "./contracts";
import { DEFAULT_SHOP_DISPLAY_NAME } from "./default-template";
import { getDefaultShopSettings } from "./repo/chat-tools";

export type OrderSummaryCardVariantLine = {
  variantName: string;
  quantity: number;
  unitLabel: string;
  amountLabel: string;
  isBonus: boolean;
};

function resolveOrderItemUnitLabel(item: OrderDetailDTO["items"][number]): string {
  if (item.lineType === "ad_hoc_kit") return "ຊຸດ";
  return normalizeStockUnitLabel(item.stockUnitLabel);
}

export function formatOrderSummaryQty(qty: number, unitLabel: string): string {
  const unit = normalizeStockUnitLabel(unitLabel);
  return `x${qty.toLocaleString("lo-LA")} ${unit}`;
}

/** ກຸ່ມສິນຄ້າດຽວກັນ — ລວມຫຼາຍ variant (ຂາຍ + ແຖມ) ໄວ້ດ້ານລຸ່ມ */
export type OrderSummaryCardProductGroup = {
  productName: string;
  variants: OrderSummaryCardVariantLine[];
};

export type OrderSummaryCardViewModel = {
  shopName: string;
  orderNumber: string;
  customerName: string;
  phone: string | null;
  deliverySummary: string;
  address: string | null;
  itemGroups: OrderSummaryCardProductGroup[];
  subtotal: string;
  discount: string | null;
  shipping: string | null;
  total: string;
  paymentLabel: string;
  bankLines: string[];
};

function buildBankLines(
  order: OrderDetailDTO,
  settings: ShopSettingsDTO,
): string[] {
  if (order.paymentType === "cod") {
    return ["COD — ຈ່າຍເມື່ອຮັບສິນຄ້າ"];
  }

  const lines: string[] = [];
  if (
    settings.bankName ||
    settings.bankAccountNumber ||
    settings.bankAccountName
  ) {
    lines.push("ໂອນເຂົ້າບັນຊີ:");
    if (settings.bankName && settings.bankAccountNumber) {
      lines.push(`ທະນາຄານ ${settings.bankName} ${settings.bankAccountNumber}`);
    } else if (settings.bankAccountNumber) {
      lines.push(settings.bankAccountNumber);
    }
    if (settings.bankAccountName) {
      lines.push(`ຊື່ບັນຊີ: ${settings.bankAccountName}`);
    }
  }
  return lines;
}

/** ຈັດກຸ່ມຕາມຊື່ສິນຄ້າ — ຂາຍ/ແຖມຢູ່ກຸ່ມດຽວກັນ ຮັກສາລຳດັບທີ່ປະກົດຄັ້ງທຳອິດ */
export function groupOrderSummaryItems(
  order: OrderDetailDTO,
): OrderSummaryCardProductGroup[] {
  const groups: OrderSummaryCardProductGroup[] = [];
  const indexByKey = new Map<string, number>();

  for (const item of order.items) {
    const key = item.productName;
    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(key, idx);
      groups.push({
        productName: item.productName,
        variants: [],
      });
    }
    groups[idx]?.variants.push({
      variantName: item.variantName,
      quantity: item.quantity,
      unitLabel: resolveOrderItemUnitLabel(item),
      amountLabel: item.isBonus ? "ແຖມ" : formatLak(item.lineTotal),
      isBonus: item.isBonus,
    });
  }

  return groups;
}

export function formatGroupedOrderLinesText(
  groups: OrderSummaryCardProductGroup[],
): string {
  return groups
    .map((group) => {
      const lines = group.variants
        .map((v) => {
          const bonusTag = v.isBonus ? " [ແຖມ]" : "";
          const qtyLabel = formatOrderSummaryQty(v.quantity, v.unitLabel);
          return `  - ${v.variantName}${bonusTag} ${qtyLabel} = ${v.amountLabel}`;
        })
        .join("\n");
      return `• ${group.productName}\n${lines}`;
    })
    .join("\n");
}

export function buildOrderSummaryCardViewModel(
  order: OrderDetailDTO,
  settings?: ShopSettingsDTO,
): OrderSummaryCardViewModel {
  const shop = settings ?? getDefaultShopSettings();
  const shippingFee = Number(order.shippingFee);
  const discountAmount = Number(order.discountAmount);

  return {
    shopName: shop.shopDisplayName.trim() || DEFAULT_SHOP_DISPLAY_NAME,
    orderNumber: order.orderNumber,
    customerName: order.customer.name,
    phone: order.customer.phone?.trim() || null,
    deliverySummary: formatDeliverySummary(order),
    address: displayCustomerAddress(order.customer),
    itemGroups: groupOrderSummaryItems(order),
    subtotal: formatLak(order.subtotal),
    discount:
      discountAmount > 0 ? `-${formatLak(order.discountAmount)}` : null,
    shipping: shippingFee > 0 ? formatLak(order.shippingFee) : null,
    total: formatLak(order.total),
    paymentLabel: paymentTypeLabel(order.paymentType),
    bankLines: buildBankLines(order, shop),
  };
}
